import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { AppShell } from '@astryxdesign/core/AppShell';
import { SideNav, SideNavHeading, SideNavItem, SideNavSection } from '@astryxdesign/core/SideNav';
import { TabList, Tab } from '@astryxdesign/core/TabList';
import { Badge } from '@astryxdesign/core/Badge';
import { useMediaQuery } from '@astryxdesign/core/hooks';
import * as stylex from '@stylexjs/stylex';
import '@astryxdesign/core/theme/tokens.stylex';
import { spacingVars } from '@astryxdesign/core/theme/tokens.stylex';
import { Search } from 'lucide-react';
import { BottomTabBar, QronMark } from '@qronstat/ui';
import type { BottomTabItem } from '@qronstat/ui';
import { preloadCoverageMap } from '@qronstat/ui/map';
import { sectionPermissions } from '@qronstat/shared';
import { useUnreadAlerts } from '../api/references';
import { filtersEvent } from '../screens/ScreenHeader';
import { useApp } from './context';
import { mobileQuery } from './layout';
import { navSections, sectionForPath, tabForPath, tabs, visibleLeaves } from './navigation';
import type { TabKey } from './navigation';
import { arrivalScroll, remember, tabTarget } from './tabMemory';
import type { ArrivalReason } from './tabMemory';
import { preloadWidgets } from '../screens/WidgetRenderer';
import { StatusBanners } from './StatusBanners';
import { MoreSheet } from './MoreSheet';
import { openSearchEvent } from './events';
const SearchPalette = lazy(() => import('../components/SearchPalette').then(m => ({
  default: m.SearchPalette
})));
const scrollerId = 'astryx-app-shell-main';
function scroller(): HTMLElement | null {
  return document.getElementById(scrollerId);
}
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.getAttribute('role') === 'combobox';
}
export function Shell({
  children
}: {
  children: ReactNode;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    can
  } = useApp();
  const router = useRouter();
  const pathname = useRouterState({
    select: s => s.location.pathname
  });
  const href = useRouterState({
    select: s => s.resolvedLocation?.href ?? s.location.href
  });
  const remembered = useRef(new Map<TabKey, string>());
  const scrolls = useRef(new Map<string, number>());
  const arrival = useRef<ArrivalReason>('push');
  const [pendingTab, setPendingTab] = useState<TabKey | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const isMobile = useMediaQuery(mobileQuery);
  const [searchOpen, setSearchOpen] = useState(false);
  const unread = useUnreadAlerts(can(sectionPermissions.alerts));
  const unreadCount = unread.data?.count ?? 0;
  const sections = useMemo(() => navSections.filter(s => !s.permission || can(s.permission)), [can]);
  const section = sectionForPath(pathname);
  const activeTab = tabForPath(pathname);
  useEffect(() => router.subscribe('onBeforeNavigate', event => {
    const main = scroller();
    if (main && event.fromLocation) remember(scrolls.current, event.fromLocation.href, main.scrollTop);
  }), [router]);
  useEffect(() => router.history.subscribe(({
    action
  }) => {
    if (action.type === 'BACK' || action.type === 'FORWARD') arrival.current = 'back';
  }), [router]);
  useLayoutEffect(() => {
    remember(remembered.current, tabForPath(new URL(href, 'http://local').pathname), href);
    const target = arrivalScroll(href, arrival.current, scrolls.current);
    arrival.current = 'push';
    setPendingTab(null);
    setMoreOpen(false);
    const main = scroller();
    if (!main) return;
    main.scrollTop = target;
    if (target <= 0) return;
    let frames = 0;
    let frame = requestAnimationFrame(function settle() {
      if (Math.abs(main.scrollTop - target) > 1 && frames++ < 30) {
        main.scrollTop = target;
        frame = requestAnimationFrame(settle);
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [href]);
  const closeMore = useCallback(() => setMoreOpen(false), []);
  const selectTab = (item: BottomTabItem) => {
    if (item.key === 'more') {
      setMoreOpen(open => !open);
      return;
    }
    setMoreOpen(false);
    const tab = tabs.find(t => t.key === item.key);
    if (!tab) return;
    if (tab.key === activeTab) {
      if (pathname === tab.path) scroller()?.scrollTo({
        top: 0,
        behavior: 'smooth'
      });else void navigate({
        to: tab.path
      });
      return;
    }
    arrival.current = 'tab';
    setPendingTab(tab.key);
    void navigate({
      href: tabTarget(tab, activeTab, remembered.current)
    });
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      if (e.key === '/') {
        e.preventDefault();
        setSearchOpen(true);
      } else if (e.code === 'KeyF') {
        e.preventDefault();
        window.dispatchEvent(new Event(filtersEvent));
      }
    };
    const openSearch = () => setSearchOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(openSearchEvent, openSearch);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(openSearchEvent, openSearch);
    };
  }, []);
  useEffect(() => {
    const connection = (navigator as unknown as {
      connection?: {
        saveData?: boolean;
      };
    }).connection;
    if (isMobile || connection?.saveData) return;
    const idle = (window as unknown as {
      requestIdleCallback?: (cb: () => void) => number;
    }).requestIdleCallback;
    const run = () => void preloadCoverageMap().catch(() => undefined);
    if (idle) idle(run);else setTimeout(run, 2000);
  }, [isMobile]);
  useEffect(() => {
    const idle = (window as unknown as {
      requestIdleCallback?: (cb: () => void) => number;
    }).requestIdleCallback;
    const run = () => void preloadWidgets();
    if (idle) idle(run);else setTimeout(run, 1500);
  }, []);
  const sideNav = isMobile ? undefined : <SideNav collapsible={{
    buttonLabel: intl.formatMessage({
      id: 'nav.collapse'
    })
  }} header={<SideNavHeading heading={intl.formatMessage({
    id: 'app.name'
  })} headingHref="/" icon={<QronMark size={28} />} />}>
      <SideNavSection title={intl.formatMessage({
      id: 'nav.label'
    })} isHeaderHidden>
        <SideNavItem label={intl.formatMessage({
        id: 'search.open'
      })} icon={Search} onClick={() => setSearchOpen(true)} />
        {sections.map(s => <SideNavItem key={s.key} label={intl.formatMessage({
        id: s.titleKey
      })} href={s.path} icon={s.icon} isSelected={s.children.length === 0 && section?.key === s.key} collapsible={s.children.length > 0 ? {
        defaultIsCollapsed: section?.key !== s.key
      } : false} {...s.key === 'alerts' && unreadCount > 0 ? {
        endContent: <Badge label={String(unreadCount)} variant="warning" />
      } : {}}>
            {s.children.length > 0 ? visibleLeaves(s.children, can).map(leaf => <SideNavItem key={leaf.key} label={intl.formatMessage({
          id: leaf.titleKey
        })} href={leaf.path} isSelected={pathname === leaf.path} />) : undefined}
          </SideNavItem>)}
      </SideNavSection>
    </SideNav>;
  const visibleTabs = tabs.filter(t => {
    if (t.key === 'more') return true;
    const s = navSections.find(x => x.key === t.key);
    return !s?.permission || can(s.permission);
  });
  return <AppShell sideNav={sideNav} mobileNav={false} height="fill" variant="elevated">
      <div {...{
      0: {
        className: "x1us19tq x78zum5 xdt5ytf xq1krc4 xopnk0n"
      },
      1: {
        className: "x1us19tq x78zum5 xdt5ytf xq1krc4 xopnk0n x1z0oos6"
      }
    }[!!isMobile << 0]}>
        <StatusBanners />
        {isMobile && section && section.children.length > 0 ? <div className="x1pzlopt x1xye8es xw2csxc x1rohswg">
            <TabList value={pathname} onChange={(v: string) => void navigate({
          to: v
        })} size="sm" hasDivider>
              {visibleLeaves(section.children, can).map(leaf => <Tab key={leaf.key} value={leaf.path} label={intl.formatMessage({
            id: leaf.titleKey
          })} />)}
            </TabList>
          </div> : null}
        {children}
      </div>
      {isMobile ? <MoreSheet open={moreOpen} onClose={closeMore} /> : null}
      {isMobile ? <BottomTabBar label={intl.formatMessage({
      id: 'nav.tabs'
    })} onSelect={selectTab} items={visibleTabs.map(t => ({
      key: t.key,
      label: intl.formatMessage({
        id: t.titleKey
      }),
      href: tabTarget(t, activeTab, remembered.current),
      icon: t.icon,
      isActive: t.key === (moreOpen ? 'more' : pendingTab ?? activeTab),
      ...(t.key === 'more' && unreadCount > 0 ? {
        badge: unreadCount
      } : {})
    }))} /> : null}
      {searchOpen ? <Suspense fallback={null}>
          <SearchPalette isOpen={searchOpen} onOpenChange={setSearchOpen} />
        </Suspense> : null}
    </AppShell>;
}